/**
 * Integrations API Client - HTTP client for integration endpoints
 */

import type {
  GitHubIntegrationState,
  JiraIntegrationState,
  GitHubInstallUrlResponse,
} from '@resolve-ai/shared';

import { fetchWithAuth } from './api-client';

// GitHub Integration API

/**
 * GitHub repository type
 */
export interface Repository {
  id: number;
  name: string;
  full_name: string;
  owner: { login: string };
  default_branch: string;
  private: boolean;
  html_url: string;
}

/**
 * Jira project type
 */
export interface JiraProject {
  id: string;
  key: string;
  name: string;
  projectTypeKey: string;
  simplified: boolean;
  style: string;
  self: string;
}

/**
 * Jira user type
 */
export interface JiraUser {
  accountId: string;
  displayName: string;
  emailAddress?: string;
  active: boolean;
}

/**
 * Jira status type
 */
export interface JiraStatus {
  id: string;
  name: string;
  statusCategory: {
    name: string;
    key: string;
  };
}

/**
 * Integrations API client
 */
export const integrationsApi = {
  /**
   * Get GitHub connection status
   */
  async getGitHubStatus(): Promise<GitHubIntegrationState> {
    return fetchWithAuth<GitHubIntegrationState>('/integrations/github/status');
  },

  /**
   * Get GitHub App installation URL
   */
  async getGitHubInstallUrl(returnUrl: string, skipReconnect?: boolean): Promise<GitHubInstallUrlResponse> {
    const queryParams = new URLSearchParams({ return_url: returnUrl });
    if (skipReconnect) {
      queryParams.set('skip_reconnect', 'true');
    }
    return fetchWithAuth<GitHubInstallUrlResponse>(`/integrations/github/install?${queryParams}`);
  },

  /**
   * Disconnect GitHub integration
   */
  async disconnectGitHub(): Promise<void> {
    await fetchWithAuth<{ message: string }>('/integrations/github', {
      method: 'DELETE',
    });
  },

  /**
   * List accessible GitHub repositories
   */
  async getRepositories(): Promise<Repository[]> {
    const response = await fetchWithAuth<{ repositories: Repository[] }>(
      '/integrations/github/repositories'
    );
    return response.repositories;
  },

  /**
   * List branches for a GitHub repository
   */
  async getBranches(owner: string, repo: string): Promise<{ name: string }[]> {
    const response = await fetchWithAuth<{ branches: { name: string }[] }>(
      `/integrations/github/repositories/${owner}/${repo}/branches`
    );
    return response.branches;
  },

  // Jira Integration API

  /**
   * Get Jira connection status
   */
  async getJiraStatus(): Promise<JiraIntegrationState> {
    return fetchWithAuth<JiraIntegrationState>('/integrations/jira/status');
  },

  /**
   * Get Jira OAuth connect URL
   */
  async getJiraConnectUrl(returnUrl: string): Promise<{ url: string }> {
    const queryParams = new URLSearchParams({ return_url: returnUrl });
    return fetchWithAuth<{ url: string }>(`/integrations/jira/connect?${queryParams}`);
  },

  /**
   * Disconnect Jira integration
   */
  async disconnectJira(): Promise<void> {
    await fetchWithAuth<{ message: string }>('/integrations/jira', {
      method: 'DELETE',
    });
  },

  /**
   * List accessible Jira projects
   */
  async getJiraProjects(): Promise<JiraProject[]> {
    const response = await fetchWithAuth<{ projects: JiraProject[] }>(
      '/integrations/jira/projects'
    );
    return response.projects;
  },

  /**
   * Get assignable users for a Jira project
   */
  async getJiraAssignableUsers(projectKey: string): Promise<JiraUser[]> {
    const response = await fetchWithAuth<{ users: JiraUser[] }>(
      `/integrations/jira/projects/${encodeURIComponent(projectKey)}/users`
    );
    return response.users;
  },

  /**
   * Get statuses for a Jira project
   */
  async getJiraProjectStatuses(projectKey: string): Promise<JiraStatus[]> {
    const response = await fetchWithAuth<{ statuses: JiraStatus[] }>(
      `/integrations/jira/projects/${encodeURIComponent(projectKey)}/statuses`
    );
    return response.statuses;
  },
};
